import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion, type MotionValue } from 'framer-motion';
import {
  ChevronLeft,
  ChevronRight,
  Maximize2,
  Minimize2,
  Pause,
  Play,
  Settings2,
  Video,
  X,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { Theme, VisualizerMode } from '../../types';
import type {
  VisualizerBackgroundActions,
  VisualizerBackgroundConfig,
} from '../../components/visualizer/backgrounds/definition';
import ProgressBar from '../../components/ProgressBar';
import LyricStageAppearanceControls from './LyricStageAppearanceControls';
import type LyricStageSettingsPanel from '../LyricStageSettingsPanel';

// packages/lyric-stage/src/host/chrome/LyricStagePlayerChrome.tsx
// Folia 歌词动画页：底部折叠进度条 + 右下角 Settings2 面板（对标 UnifiedPanel / FloatingPlayerControls）。

export type LyricStageRecordPhase =
  | 'preparing'
  | 'countdown'
  | 'recording'
  | 'stopping'
  | 'done'
  | 'cancelled'
  | 'error';

export type LyricStageRecordStatus = {
  phase: LyricStageRecordPhase;
  countdown: number | null;
  elapsed: number;
  duration: number;
  error: string | null;
};

const SETTINGS_GUIDE_STORAGE_KEY = 'musey-lyric-stage-settings-guide-dismissed';

const readSettingsGuideDismissed = () => {
  if (typeof window === 'undefined') return true;
  try {
    return window.localStorage.getItem(SETTINGS_GUIDE_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
};

const readIsFullscreen = () => (
  typeof document !== 'undefined' && Boolean(document.fullscreenElement)
);

/** 切换浏览器全屏（去掉顶栏，页面占满屏幕） */
const toggleBrowserFullscreen = async () => {
  if (typeof document === 'undefined') return false;
  try {
    if (document.fullscreenElement) {
      await document.exitFullscreen();
      return true;
    }
    await document.documentElement.requestFullscreen();
    return true;
  } catch (error) {
    console.warn('[LyricStagePlayerChrome] Failed to toggle browser fullscreen:', error);
    return false;
  }
};

const formatClock = (seconds: number) => {
  const s = Math.max(0, Math.floor(seconds));
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return `${m}:${rem < 10 ? '0' : ''}${rem}`;
};

export interface LyricStageQueueItem {
  id: string;
  title: string;
}

export interface LyricStagePlayerChromeProps {
  theme: Theme;
  onThemeChange?: (theme: Theme) => void;
  isDaylight?: boolean;
  onDaylightChange?: (isDaylight: boolean) => void;
  visualizerMode: VisualizerMode;
  onVisualizerModeChange: (mode: VisualizerMode) => void;
  backgroundConfig: VisualizerBackgroundConfig;
  backgroundActions: VisualizerBackgroundActions;
  currentTime: MotionValue<number>;
  duration: number;
  paused: boolean;
  onTogglePlay: () => void;
  onSeek: (time: number) => void;
  coverUrl?: string | null;
  songTitle?: string | null;
  songArtist?: string | null;
  tracks?: LyricStageQueueItem[];
  currentTrackId?: string | null;
  onSelectTrack?: (id: string) => void;
  onPrevTrack?: () => void;
  onNextTrack?: () => void;
  onExitLyricStage?: () => void;
  /** Hide playbar / settings while recording PV */
  chromeHidden?: boolean;
  /** Stage-only crop target for PV recording */
  onStageSurfaceReady?: (el: HTMLElement | null) => void;
  recordSupported?: boolean;
  recordStatus?: LyricStageRecordStatus | null;
  /** When false, host shows PV HUD outside CropTarget (iframe capture). Default true. */
  showRecordHud?: boolean;
  onStartPvRecord?: () => void;
  onCancelPvRecord?: () => void;
  settingsPanelExtra?: React.ComponentProps<typeof LyricStageSettingsPanel>;
  /** 模式自带的额外控件，放在设置面板的滚动区域内 */
  appearancePanelExtra?: React.ReactNode;
  appearanceOverrides?: {
    visualizerBadges?: Record<string, string>;
    visualizerQuickControl?: React.ReactNode;
    backgroundRowOverride?: React.ReactNode;
    hideDaylightControls?: boolean;
  };
  /** 设置面板底部额外内容 */
  panelFooter?: React.ReactNode;
  /** 额外入口，例如 demo 的数据面板开关 */
  trailingFab?: React.ReactNode;
  children: React.ReactNode;
}

const LyricStagePlayerChrome: React.FC<LyricStagePlayerChromeProps> = ({
  theme,
  onThemeChange,
  isDaylight = false,
  onDaylightChange,
  visualizerMode,
  onVisualizerModeChange,
  backgroundConfig,
  backgroundActions,
  currentTime,
  duration,
  paused,
  onTogglePlay,
  onSeek,
  coverUrl,
  songTitle,
  songArtist,
  tracks = [],
  currentTrackId = null,
  onSelectTrack,
  onPrevTrack,
  onNextTrack,
  onExitLyricStage,
  chromeHidden = false,
  onStageSurfaceReady,
  recordSupported = false,
  recordStatus = null,
  showRecordHud = true,
  onStartPvRecord,
  onCancelPvRecord,
  settingsPanelExtra,
  appearancePanelExtra,
  appearanceOverrides,
  panelFooter,
  trailingFab,
  children,
}) => {
  const { t } = useTranslation();
  const stageRef = useRef<HTMLDivElement | null>(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [barHovered, setBarHovered] = useState(false);
  const [settingsHot, setSettingsHot] = useState(false);
  const [showSettingsGuide, setShowSettingsGuide] = useState(() => !readSettingsGuideDismissed());
  const [isFullscreen, setIsFullscreen] = useState(readIsFullscreen);

  useEffect(() => {
    const onFullscreenChange = () => setIsFullscreen(readIsFullscreen());
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
  }, []);

  const dismissSettingsGuide = () => {
    if (!showSettingsGuide) return;
    setShowSettingsGuide(false);
    try {
      window.localStorage.setItem(SETTINGS_GUIDE_STORAGE_KEY, '1');
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    onStageSurfaceReady?.(stageRef.current);
    return () => onStageSurfaceReady?.(null);
  }, [onStageSurfaceReady]);

  useEffect(() => {
    if (chromeHidden) setPanelOpen(false);
  }, [chromeHidden]);

  const glassBg = isDaylight ? 'bg-white/60' : 'bg-black/40';
  const glassCollapsed = isDaylight
    ? 'bg-white/40 border border-white/20'
    : 'bg-black/20 border border-white/5';
  const glassExpanded = isDaylight
    ? 'bg-white/60 border border-white/20'
    : 'bg-black/40 border border-white/5';
  const trackColor = isDaylight ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.1)';
  const textMuted = isDaylight ? 'text-zinc-700' : 'text-white/70';
  const chromeInk = isDaylight ? 'text-zinc-800/70' : 'text-white/70';

  const safeDuration = Math.max(duration, 0.01);

  const metaLabel = useMemo(
    () => songTitle || t('ui.nowPlaying'),
    [songTitle, t],
  );

  const showSkip = Boolean(onPrevTrack || onNextTrack);
  const recordingActive = Boolean(
    recordStatus
    && (recordStatus.phase === 'preparing'
      || recordStatus.phase === 'countdown'
      || recordStatus.phase === 'recording'
      || recordStatus.phase === 'stopping'),
  );
  // CropTarget 裁的是元素包围盒内的标签页像素；HUD 叠在舞台上会被录进画面。
  // 同文档录制：顶部留条，surface 下移。跨源 iframe 录制：宿主侧 HUD，此处不画、不占位。
  const reserveHudStrip = showRecordHud && (chromeHidden || recordingActive);

  return (
    <div className="relative h-full w-full overflow-hidden">
      {showRecordHud && recordingActive && (
        <div className="ls-pv-record-hud pointer-events-auto absolute inset-x-0 top-0 z-50 flex h-14 items-center justify-center px-3">
          <div className="flex items-center gap-3 rounded-full border border-white/15 bg-black/55 px-4 py-2 text-white shadow-lg backdrop-blur-md">
            {recordStatus?.phase === 'countdown' || recordStatus?.phase === 'preparing' ? (
              <span className="text-sm font-semibold tracking-wide">
                {recordStatus?.phase === 'preparing'
                  ? t('ui.recordNeedTab')
                  : t('ui.recordCountdown', { n: recordStatus.countdown ?? 0 })}
              </span>
            ) : (
              <>
                <span className="h-2 w-2 animate-pulse rounded-full bg-red-400" />
                <span className="text-sm font-medium tracking-wide">
                  {t('ui.recording')}
                  {' '}
                  {formatClock(recordStatus?.elapsed || 0)}
                  {' / '}
                  {formatClock(recordStatus?.duration || 0)}
                </span>
              </>
            )}
            {onCancelPvRecord && (
              <button
                type="button"
                className="rounded-full bg-white/15 px-2.5 py-1 text-xs font-semibold hover:bg-white/25"
                onClick={onCancelPvRecord}
              >
                {t('ui.cancelRecord')}
              </button>
            )}
          </div>
        </div>
      )}

      <div
        ref={stageRef}
        data-lyric-stage-surface
        className={`absolute inset-x-0 bottom-0 ${reserveHudStrip ? 'top-14' : 'top-0'}`}
      >
        {children}
      </div>

      {!chromeHidden && (
        <div className="pointer-events-none absolute inset-x-0 bottom-0 z-30 flex items-end justify-center px-4 pb-8">
          <div
            className="pointer-events-auto relative flex w-full max-w-lg justify-center"
            onMouseEnter={() => setBarHovered(true)}
            onMouseLeave={() => setBarHovered(false)}
          >
            <motion.div
              layout
              className={`w-full overflow-hidden rounded-full shadow-2xl backdrop-blur-xl transition-colors duration-300 ${
                barHovered ? `p-3 ${glassExpanded}` : `w-[80%] px-4 py-2 md:w-[60%] ${glassCollapsed}`
              }`}
            >
              {barHovered ? (
                <div className="flex w-full items-center gap-3">
                  <button
                    type="button"
                    onClick={onTogglePlay}
                    className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${
                      isDaylight ? 'bg-black/10 text-zinc-900' : 'bg-white/10 text-white'
                    }`}
                    aria-label={paused ? t('ui.play') : t('ui.pause')}
                  >
                    {paused ? <Play size={16} fill="currentColor" /> : <Pause size={16} fill="currentColor" />}
                  </button>
                  <div className="min-w-0 flex-1">
                    <div className="mb-1 flex items-center gap-1">
                      {showSkip && (
                        <button
                          type="button"
                          onClick={() => onPrevTrack?.()}
                          disabled={!onPrevTrack}
                          className={`flex h-5 w-5 shrink-0 items-center justify-center transition-opacity ${chromeInk} opacity-70 hover:opacity-100 disabled:opacity-25`}
                          aria-label={t('ui.previousTrack')}
                          title={t('ui.previousTrack')}
                        >
                          <ChevronLeft size={16} strokeWidth={1.75} />
                        </button>
                      )}
                      <div
                        className="min-w-0 flex-1 truncate text-center text-xs font-medium"
                        style={{ color: theme.primaryColor }}
                      >
                        {metaLabel}
                        {songArtist ? <span className="opacity-50"> · {songArtist}</span> : null}
                      </div>
                      {showSkip && (
                        <button
                          type="button"
                          onClick={() => onNextTrack?.()}
                          disabled={!onNextTrack}
                          className={`flex h-5 w-5 shrink-0 items-center justify-center transition-opacity ${chromeInk} opacity-70 hover:opacity-100 disabled:opacity-25`}
                          aria-label={t('ui.nextTrack')}
                          title={t('ui.nextTrack')}
                        >
                          <ChevronRight size={16} strokeWidth={1.75} />
                        </button>
                      )}
                    </div>
                    <ProgressBar
                      currentTime={currentTime}
                      duration={safeDuration}
                      onSeek={onSeek}
                      primaryColor={theme.primaryColor}
                      secondaryColor={theme.secondaryColor}
                      trackColor={trackColor}
                    />
                  </div>
                </div>
              ) : (
                <div className="flex h-8 w-full items-center justify-center px-4">
                  <ProgressBar
                    currentTime={currentTime}
                    duration={safeDuration}
                    onSeek={onSeek}
                    primaryColor={theme.primaryColor}
                    secondaryColor={theme.secondaryColor}
                    trackColor={trackColor}
                  />
                </div>
              )}
            </motion.div>
          </div>
        </div>
      )}

      {!chromeHidden && (
        <div className="absolute bottom-5 right-5 z-40 flex flex-col items-end gap-3">
          <AnimatePresence>
            {panelOpen && (
              <motion.div
                initial={{ opacity: 0, y: 16, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 16, scale: 0.96 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
                className={`ls-settings-panel w-[min(340px,calc(100vw-2rem))] overflow-hidden rounded-[28px] border border-white/10 shadow-2xl backdrop-blur-xl ${glassBg} ${
                  isDaylight ? 'text-zinc-900' : 'text-white'
                }`}
              >
                <div className="flex max-h-[min(72vh,36rem)] flex-col">
                  {(coverUrl || songTitle) && (
                    <div className="flex items-center gap-3 px-4 pb-3 pt-4">
                      {coverUrl ? (
                        <img
                          src={coverUrl}
                          alt=""
                          className="h-12 w-12 shrink-0 rounded-2xl object-cover shadow-md"
                        />
                      ) : (
                        <div className={`h-12 w-12 shrink-0 rounded-2xl ${isDaylight ? 'bg-black/10' : 'bg-white/10'}`} />
                      )}
                      <div className="min-w-0">
                        <div className="ls-panel-title truncate" style={{ color: theme.primaryColor }}>
                          {metaLabel}
                        </div>
                        {songArtist && (
                          <div className={`ls-panel-subtitle truncate ${textMuted}`}>{songArtist}</div>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-2">
                    <section className="ls-panel-section">
                      <div className="ls-panel-section-label">{t('ui.playlist')}</div>
                      {tracks.length ? (
                        <div className="ls-track-list">
                          {tracks.map((track) => {
                            const active = track.id === currentTrackId;
                            return (
                              <button
                                key={track.id}
                                type="button"
                                className={`ls-track-item ${active ? 'is-active' : ''}`}
                                style={active ? { color: theme.primaryColor } : undefined}
                                onClick={() => onSelectTrack?.(track.id)}
                              >
                                <span className="truncate">{track.title}</span>
                              </button>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="ls-track-empty">{t('ui.emptyPlaylist')}</div>
                      )}
                    </section>

                    <section className="ls-panel-section">
                      <div className="ls-panel-section-label">{t('ui.stage')}</div>
                      <LyricStageAppearanceControls
                        theme={theme}
                        onThemeChange={onThemeChange}
                        isDaylight={isDaylight}
                        onDaylightChange={onDaylightChange}
                        visualizerMode={visualizerMode}
                        onVisualizerModeChange={onVisualizerModeChange}
                        backgroundConfig={backgroundConfig}
                        backgroundActions={backgroundActions}
                        settingsPanelExtra={settingsPanelExtra}
                        {...appearanceOverrides}
                      />
                      {appearancePanelExtra}
                    </section>
                  </div>

                  <div className="ls-panel-actions ls-panel-actions-triple px-4 pb-4 pt-1">
                    <button
                      type="button"
                      className="ls-panel-action"
                      onClick={() => void toggleBrowserFullscreen()}
                    >
                      {isFullscreen ? <Minimize2 size={15} /> : <Maximize2 size={15} />}
                      <span>{isFullscreen ? t('ui.exitFullscreen') : t('ui.browserFullscreen')}</span>
                    </button>
                    {onStartPvRecord && (
                      <button
                        type="button"
                        className="ls-panel-action"
                        disabled={!recordSupported || recordingActive}
                        title={recordSupported ? t('ui.recordPv') : t('ui.recordUnsupported')}
                        onClick={() => {
                          setPanelOpen(false);
                          onStartPvRecord();
                        }}
                      >
                        <Video size={15} />
                        <span>{t('ui.recordPv')}</span>
                      </button>
                    )}
                    {onExitLyricStage && (
                      <button
                        type="button"
                        className="ls-panel-action ls-panel-action-exit"
                        onClick={onExitLyricStage}
                      >
                        <X size={15} />
                        <span>{t('ui.exitLyricStage')}</span>
                      </button>
                    )}
                  </div>
                  {panelFooter}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {trailingFab}

          <div
            className="relative flex h-11 items-center justify-end"
            style={{ paddingLeft: 40 }}
            onMouseEnter={() => setSettingsHot(true)}
            onMouseLeave={() => setSettingsHot(false)}
          >
            <AnimatePresence>
              {showSettingsGuide && !panelOpen && (
                <motion.div
                  key="settings-guide"
                  initial={{ opacity: 0, y: 10, scale: 0.96 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 6, scale: 0.98 }}
                  transition={{ duration: 0.28, ease: 'easeOut' }}
                  className="absolute bottom-[calc(100%+14px)] right-0 z-50 w-[min(17.5rem,calc(100vw-2.5rem))]"
                >
                  <button
                    type="button"
                    className={`ls-settings-guide group relative w-full rounded-2xl border px-4 py-3.5 text-left shadow-2xl backdrop-blur-xl transition-transform duration-200 hover:-translate-y-0.5 ${
                      isDaylight
                        ? 'border-black/10 bg-white/92 text-zinc-900'
                        : 'border-white/15 bg-zinc-950/88 text-white'
                    }`}
                    onClick={dismissSettingsGuide}
                  >
                    <div className="mb-1.5 flex items-center justify-between gap-3">
                      <span
                        className="text-[13px] font-semibold tracking-wide"
                        style={{ color: theme.primaryColor }}
                      >
                        {t('ui.settingsGuideTitle')}
                      </span>
                      <span
                        className={`rounded-full px-2 py-0.5 text-[10px] font-semibold tracking-wider ${
                          isDaylight ? 'bg-black/6 text-zinc-500' : 'bg-white/10 text-white/55'
                        }`}
                      >
                        {t('ui.settingsGuideAction')}
                      </span>
                    </div>
                    <p className={`text-[12px] leading-relaxed ${isDaylight ? 'text-zinc-600' : 'text-white/70'}`}>
                      {t('ui.settingsGuideBody')}
                    </p>
                    <span
                      className={`pointer-events-none absolute -bottom-1.5 right-5 h-3 w-3 rotate-45 border-b border-r ${
                        isDaylight
                          ? 'border-black/10 bg-white/92'
                          : 'border-white/15 bg-zinc-950/88'
                      }`}
                      aria-hidden="true"
                    />
                  </button>
                </motion.div>
              )}
            </AnimatePresence>

            <button
              type="button"
              onClick={() => {
                dismissSettingsGuide();
                setPanelOpen(v => !v);
              }}
              className={`relative flex h-11 items-center justify-center rounded-full shadow-lg backdrop-blur-md transition-[width,padding,background-color,color,box-shadow] duration-200 ease-out ${
                settingsHot || panelOpen || showSettingsGuide ? 'w-[5.25rem]' : 'w-11'
              } ${
                panelOpen
                  ? 'bg-white text-black'
                  : isDaylight
                    ? 'bg-white/70 text-zinc-900'
                    : 'bg-black/40 text-white'
              } ${
                showSettingsGuide && !panelOpen
                  ? 'ring-2 ring-offset-2 ring-offset-transparent'
                  : ''
              }`}
              style={
                showSettingsGuide && !panelOpen
                  ? { boxShadow: `0 0 0 2px ${theme.primaryColor}55, 0 10px 30px rgba(0,0,0,0.25)` }
                  : undefined
              }
              aria-label={panelOpen ? t('ui.close') : t('ui.moreSettings')}
            >
              {showSettingsGuide && !panelOpen && (
                <span
                  className="pointer-events-none absolute inset-0 rounded-full"
                  style={{
                    boxShadow: `0 0 0 0 ${theme.primaryColor}`,
                    animation: 'ls-settings-guide-pulse 1.8s ease-out infinite',
                  }}
                  aria-hidden="true"
                />
              )}
              {panelOpen ? <X size={18} /> : <Settings2 size={18} />}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LyricStagePlayerChrome;
